import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { XCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { GoogleIcon, Spinner } from "@/components/peeps/ui";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors, themeOf, gradientCss } from "@/lib/theme";
import logo from "@/assets/peeps-logo.png";

export default function Invite() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading, login, signingIn } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.invitePreview(String(code))
      .then(setPreview)
      .catch(() => toast("This invite link is no longer valid"))
      .finally(() => setLoadingPreview(false));
  }, [code]);

  const accept = useCallback(async () => {
    setJoining(true);
    try {
      const res = await api.acceptInvite(String(code));
      navigate(`/group/${res.group_id}`, { replace: true });
    } catch (e) {
      toast(e.message || "Couldn't join the group");
      setJoining(false);
    }
  }, [code, navigate]);

  useEffect(() => {
    if (!loading && user && preview && !joining) accept();
  }, [loading, user, preview]); // eslint-disable-line

  const th = themeOf(preview?.theme);

  if (loadingPreview || loading) {
    return <div className="flex justify-center items-center min-h-screen" style={{ background: colors.surface }}><Spinner /></div>;
  }

  if (!preview) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 gap-2 text-center" style={{ background: colors.surface }}>
        <XCircle size={40} color={colors.muted} />
        <h1 className="font-display font-bold text-xl mt-2" style={{ color: colors.onSurface }}>Invite not found</h1>
        <p className="font-display text-sm" style={{ color: colors.onSurfaceTertiary }}>This link may have expired or the group was removed.</p>
        <button data-testid="invite-home" onClick={() => navigate("/")} className="mt-4 rounded-xl py-3.5 px-6 font-display font-bold text-[15.5px]" style={{ background: colors.brand, color: colors.onBrand }}>Go home</button>
      </div>
    );
  }

  const cover = mediaUrl(preview.cover_image_url);

  return (
    <div className="min-h-screen flex flex-col max-w-md mx-auto" style={{ background: colors.surface }}>
      <div className="relative flex-1 flex flex-col justify-end" style={cover ? {} : { background: gradientCss(th.grad) }}>
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(21,22,30,0.2), rgba(21,22,30,0.95))" }} />
        <div className="relative p-6 flex flex-col gap-2">
          <img src={logo} alt="peeps" className="w-10 h-10 rounded-xl mb-2" />
          <p className="font-display font-bold text-lg text-white opacity-90 mb-4">peeps</p>
          <p className="font-display font-medium text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>You're invited to</p>
          <h1 className="font-display font-bold text-4xl text-white leading-tight">{preview.group_name}</h1>
          <span className="flex items-center gap-1.5 self-start py-1.5 px-3 rounded-full mt-1" style={{ background: "rgba(255,255,255,0.15)" }}>
            <Users size={13} color="#fff" />
            <span className="font-display font-medium text-[12.5px] text-white">{preview.member_count} {preview.member_count === 1 ? "member" : "members"}</span>
          </span>
        </div>
      </div>

      <div className="px-6 py-6 flex flex-col gap-3">
        {user ? (
          <button data-testid="join-group-button" onClick={accept} disabled={joining} className="rounded-xl py-4 flex items-center justify-center font-display font-bold text-[15.5px]" style={{ background: th.solid, color: th.on }}>
            {joining ? <Spinner style={{ borderTopColor: th.on }} /> : `Join ${preview.group_name}`}
          </button>
        ) : (
          <>
            <button data-testid="invite-google-signin" onClick={() => login(`/invite/${code}`)} disabled={signingIn} className="flex items-center justify-center gap-2.5 rounded-xl py-4 font-display font-bold text-[15px]" style={{ background: colors.surfaceInverse, color: colors.onSurfaceInverse }}>
              {signingIn ? <Spinner style={{ borderTopColor: colors.onSurfaceInverse }} /> : (<><GoogleIcon /> Sign in with Google to join</>)}
            </button>
            <p className="font-display text-[12.5px] text-center" style={{ color: colors.faint }}>Sign in and you'll be added to the group automatically.</p>
          </>
        )}
      </div>
    </div>
  );
}
